// coopnetApi — wrappers tipados sobre apiClient para cada endpoint del
// backend de CoopNet. Se va ampliando módulo por módulo a medida que
// se conecta cada pantalla (Contabilidad, Comités, etc.).

import { api, apiFetch } from './apiClient';

const V1 = '/api/v1';

export const meApi = {
  perfil: () => api.get(`${V1}/me`),
  roles: () => api.get(`${V1}/me/roles`),
  /** Lo que el SERVIDOR ve en el token: { esSuperAdmin, rolesEfectivos } */
  permisos: () => api.get(`${V1}/me/permisos`),
};

export const geografiaApi = {
  paises: (todos = false) => api.get(`${V1}/geografia/paises?todos=${todos}`),
  departamentos: (paisId = 1045) => api.get(`${V1}/geografia/departamentos?paisId=${paisId}`),
  municipios: (departamentoId) => api.get(`${V1}/geografia/departamentos/${departamentoId}/municipios`),
};

export const organizacionApi = {
  listarConfederaciones: () => api.get(`${V1}/confederaciones`),
  // ── Confederación internacional (cuarto piso) ──
  listarInternacionales: () => api.get(`${V1}/confederaciones-internacionales`),
  obtenerInternacional: (id) => api.get(`${V1}/confederaciones-internacionales/${id}`),
  crearInternacional: (dto) => api.post(`${V1}/confederaciones-internacionales`, dto),
  actualizarInternacional: (id, dto) => api.put(`${V1}/confederaciones-internacionales/${id}`, dto),

  // ── Confederación nacional: dto = DatosConfederacionDto (país del catálogo + internacional a la que pertenece) ──
  obtenerConfederacion: (id) => api.get(`${V1}/confederaciones/${id}`),
  crearConfederacion: (dto) => api.post(`${V1}/confederaciones`, dto),
  actualizarConfederacion: (id, dto) => api.put(`${V1}/confederaciones/${id}`, dto),

  listarFederaciones: (confederacionId) =>
    api.get(`${V1}/federaciones${confederacionId ? `?confederacionId=${confederacionId}` : ''}`),
  obtenerFederacion: (federacionId) => api.get(`${V1}/federaciones/${federacionId}`),
  // dto = DatosFederacionDto (identidad, contacto, geografía del catálogo)
  crearFederacion: (confederacionId, dto) => api.post(`${V1}/confederaciones/${confederacionId}/federaciones`, dto),
  actualizarFederacion: (id, dto) => api.put(`${V1}/federaciones/${id}`, dto),

  listarCooperativas: (federacionId) =>
    api.get(`${V1}/cooperativas${federacionId ? `?federacionId=${federacionId}` : ''}`),
  obtenerCooperativa: (cooperativaId) => api.get(`${V1}/cooperativas/${cooperativaId}`),
  /** Registro de cooperativa: dto = RegistroCooperativaDto del backend (datos generales, contrato, ubicación). */
  crearCooperativa: (federacionId, dto) => api.post(`${V1}/federaciones/${federacionId}/cooperativas`, dto),
  actualizarCooperativa: (cooperativaId, dto) => api.put(`${V1}/cooperativas/${cooperativaId}`, dto),
  cambiarEstadoCooperativa: (cooperativaId, activa) => api.post(`${V1}/cooperativas/${cooperativaId}/estado`, { activa }),
  listarDocumentosCooperativa: (cooperativaId) => api.get(`${V1}/cooperativas/${cooperativaId}/documentos`),
  /** doc = { tipoDocumento: 'RUT' | 'CEDULA_REPRESENTANTE', url, nombreArchivo?, notas? } */
  registrarDocumentoCooperativa: (cooperativaId, doc) => api.post(`${V1}/cooperativas/${cooperativaId}/documentos`, doc),

  listarAfiliados: (cooperativaId) => api.get(`${V1}/cooperativas/${cooperativaId}/afiliados`),
  /** TRANSITORIO: ¿está habilitada la importación desde Ecolab/Fenagro? -> { habilitada } */
  estadoImportacionEcolab: () => api.get(`${V1}/organizacion/importacion-ecolab`),
  /** TRANSITORIO (solo SuperAdmin): entrega a CoopNet el árbol que el navegador leyó de Ecolab (ver ecolabJerarquia.js). Idempotente, no borra. */
  importarEcolab: (arbol) => api.post(`${V1}/organizacion/importar-ecolab`, arbol),

  /** Afiliados de la red, paginado. params: { cooperativaId, federacionId, confederacionId, estado, q, pagina, tamano }.
   *  El backend solo devuelve los de las cooperativas que cubren los roles del usuario. */
  listarAfiliadosRed: (params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')).toString();
    return api.get(`${V1}/afiliados${qs ? `?${qs}` : ''}`);
  },
  /** Nuevos afiliados y cooperativas por mes: [{ mes: '2026-09', afiliados, cooperativas }] */
  crecimiento: (meses = 12) => api.get(`${V1}/organizacion/resumen/crecimiento?meses=${meses}`),

  /** Totales y conteos REALES de la red (cooperativas activas, afiliados activos) por entidad. */
  resumen: () => api.get(`${V1}/organizacion/resumen`),

  obtenerAfiliado: (afiliadoId) => api.get(`${V1}/afiliados/${afiliadoId}`),
  retirarAfiliado: (afiliadoId) => api.post(`${V1}/afiliados/${afiliadoId}/retirar`),

  walletAfiliado: (afiliadoId) => api.get(`${V1}/afiliados/${afiliadoId}/wallet`),
};

export const rolesAdminApi = {
  listar: (q, limite = 50) => api.get(`${V1}/admin/usuarios?limite=${limite}${q ? `&q=${encodeURIComponent(q)}` : ''}`),
  detalle: (id) => api.get(`${V1}/admin/usuarios/${id}`),
  historial: (id, limite = 20) => api.get(`${V1}/admin/usuarios/${id}/historial?limite=${limite}`),
  /** @param {{confederacionId?:string, federacionId?:string, cooperativaId?:string}} alcance — exactamente uno según el rol */
  asignarRol: (id, rol, alcance = {}) => api.post(`${V1}/admin/usuarios/${id}/roles`, { rol, ...alcance }),
  /** Revoca por el id de la ASIGNACIÓN (no por rol) — el mismo usuario puede tener el mismo rol en varias entidades. */
  revocarRol: (asignacionId) => api.delete(`${V1}/admin/usuarios/roles/${asignacionId}`),
};

export const comitesApi = {
  listarPorCooperativa: (id) => api.get(`${V1}/cooperativas/${id}/comites`),
  listarPorFederacion: (id) => api.get(`${V1}/federaciones/${id}/comites`),
  listarPorConfederacion: (id) => api.get(`${V1}/confederaciones/${id}/comites`),
  /** nivel: 'cooperativa' | 'federacion' | 'confederacion'. body: { tipoComite, comitePadreId? } */
  crear: (nivel, id, body) => api.post(`${V1}/${nivel === 'cooperativa' ? 'cooperativas' : nivel === 'federacion' ? 'federaciones' : 'confederaciones'}/${id}/comites`, body),
  /** Plantilla del estatuto: crea los 9 tipos de comité que falten. → { creados: [...], yaExistian: [...] } */
  crearEstructura: (nivel, id) => api.post(`${V1}/${nivel === 'cooperativa' ? 'cooperativas' : nivel === 'federacion' ? 'federaciones' : 'confederaciones'}/${id}/comites/estructura`, {}),
  subcomites: (comiteId) => api.get(`${V1}/comites/${comiteId}/subcomites`),
  crearSubcomite: (comiteId, body) => api.post(`${V1}/comites/${comiteId}/subcomites`, body),
  cargos: (comiteId) => api.get(`${V1}/comites/${comiteId}/cargos`),
  /** body: { afiliadoId, cargo, numeroSuplente?, fechaInicio } */
  asignarCargo: (comiteId, body) => api.post(`${V1}/comites/${comiteId}/cargos`, body),
  finalizarCargo: (cargoId, fechaFin) => api.post(`${V1}/comites/cargos/${cargoId}/finalizar`, { fechaFin }),
  // Cargos de subcomités: 5 coordinadores (Art. 79 parágrafo y Art. 91), sin suplentes.
  cargosSubcomite: (subcomiteId) => api.get(`${V1}/subcomites/${subcomiteId}/cargos`),
  /** body: { afiliadoId, cargo, fechaInicio } */
  asignarCargoSubcomite: (subcomiteId, body) => api.post(`${V1}/subcomites/${subcomiteId}/cargos`, body),
  finalizarCargoSubcomite: (cargoId, fechaFin) => api.post(`${V1}/subcomites/cargos/${cargoId}/finalizar`, { fechaFin }),
  /** { completo: bool, faltantes: [{ cargo, numeroSuplente? }] } — 10 puestos: 5 titulares + 5 suplentes. */
  completitud: (comiteId) => api.get(`${V1}/comites/${comiteId}/completitud`),
};

// ─────────────────────────────────────────────────────────────
// Módulos financieros (todos por cooperativa). Los enumerados viajan como
// texto (JsonStringEnumConverter) y las propiedades en camelCase.
// Las fechas van como 'YYYY-MM-DD'. El período contable de la fecha debe estar abierto.
// ─────────────────────────────────────────────────────────────
const coop = (id) => `${V1}/cooperativas/${id}`;

export const contabilidadApi = {
  /** { lineasCredito, calificaciones, retenciones } — datos de referencia de los formularios */
  referencias: (id) => api.get(`${coop(id)}/contabilidad/referencias`),
  planCuentas: (id) => api.get(`${coop(id)}/contabilidad/plan-cuentas`),
  libroDiario: (id) => api.get(`${coop(id)}/contabilidad/libro-diario`),
  cuentasConAuxiliar: (id) => api.get(`${coop(id)}/contabilidad/cuentas-con-auxiliar`),
  libroAuxiliar: (id, codigo) => api.get(`${coop(id)}/contabilidad/libro-auxiliar/${encodeURIComponent(codigo)}`),
  comprobantes: (id) => api.get(`${coop(id)}/contabilidad/comprobantes`),
  /** movimientos: [{ cuenta, debito, credito, terceroId? }] */
  emitirNotaContable: (id, { concepto, soporte, movimientos }) =>
    api.post(`${coop(id)}/contabilidad/comprobantes/nota-contable`, { concepto, soporte, movimientos }),
  anularComprobante: (id, comprobanteId, motivo) =>
    api.post(`${coop(id)}/contabilidad/comprobantes/${comprobanteId}/anular`, { motivo }),
  conceptos: (id) => api.get(`${coop(id)}/contabilidad/configuracion/conceptos`),
  actualizarConcepto: (id, conceptoId, nuevaCuenta) =>
    api.put(`${coop(id)}/contabilidad/configuracion/conceptos/${encodeURIComponent(conceptoId)}`, { nuevaCuenta }),
  balanceGeneral: (id) => api.get(`${coop(id)}/contabilidad/reportes/balance-general`),
  estadoResultados: (id) => api.get(`${coop(id)}/contabilidad/reportes/estado-resultados`),
  wallet: (id) => api.get(`${coop(id)}/contabilidad/wallet`),
  excedentesAnuales: (id) => api.get(`${coop(id)}/contabilidad/wallet/excedentes-anuales`),
};

export const periodosApi = {
  listar: (id) => api.get(`${coop(id)}/periodos`),
  abrir: (id, anio, mes) => api.post(`${coop(id)}/periodos/${anio}/${mes}/abrir`),
  cerrar: (id, anio, mes) => api.post(`${coop(id)}/periodos/${anio}/${mes}/cerrar`),
};

export const tercerosApi = {
  /** tipo: 'Afiliado' | 'Proveedor' | 'Empleado' | 'EntidadFinanciera' (vacío = todos) */
  listar: (id, tipo) => api.get(`${coop(id)}/terceros${tipo ? `?tipo=${tipo}` : ''}`),
  crearProveedor: (id, body) => api.post(`${coop(id)}/terceros/proveedores`, body),
  crearEmpleado: (id, body) => api.post(`${coop(id)}/terceros/empleados`, body),
};

export const aportesApi = {
  listar: (id, limite = 100) => api.get(`${coop(id)}/aportes?limite=${limite}`),
  emitir: (id, body) => api.post(`${coop(id)}/aportes`, body),
  retirar: (id, body) => api.post(`${coop(id)}/aportes/retiro`, body),
};

export const creditosApi = {
  listar: (id) => api.get(`${coop(id)}/creditos`),
  cuotas: (id, creditoId) => api.get(`${coop(id)}/creditos/${creditoId}/cuotas`),
  desembolsar: (id, body) => api.post(`${coop(id)}/creditos/desembolso`, body),
  pagarCuota: (id, creditoId, body) => api.post(`${coop(id)}/creditos/${creditoId}/pagar-cuota`, body),
  provisionar: (id, creditoId, body) => api.post(`${coop(id)}/creditos/${creditoId}/provision`, body),
  reclasificar: (id, creditoId, body) => api.post(`${coop(id)}/creditos/${creditoId}/reclasificar`, body),
  castigar: (id, creditoId, body) => api.post(`${coop(id)}/creditos/${creditoId}/castigar`, body),
};

export const gastosApi = {
  registrarGasto: (id, body) => api.post(`${coop(id)}/gastos`, body),
  causarCompra: (id, body) => api.post(`${coop(id)}/compras/causacion`, body),
  pagarProveedor: (id, body) => api.post(`${coop(id)}/compras/pagar-proveedor`, body),
  pagarRetencion: (id, body) => api.post(`${coop(id)}/compras/pagar-retencion`, body),
};

export const nominaApi = {
  empleados: (id) => api.get(`${coop(id)}/nomina/empleados`),
  crearEmpleado: (id, body) => api.post(`${coop(id)}/nomina/empleados`, body),
  causar: (id, empleadoId, body) => api.post(`${coop(id)}/nomina/empleados/${empleadoId}/causar`, body),
  pagar: (id, body) => api.post(`${coop(id)}/nomina/pagar`, body),
  pagarSeguridadSocial: (id, body) => api.post(`${coop(id)}/nomina/pagar-seguridad-social`, body),
};

export const activosApi = {
  listar: (id) => api.get(`${coop(id)}/activos-fijos`),
  registrar: (id, body) => api.post(`${coop(id)}/activos-fijos`, body),
  depreciacionMensual: (id, body) => api.post(`${coop(id)}/activos-fijos/depreciacion-mensual`, body),
};

export const proyectosApi = {
  listar: (id) => api.get(`${coop(id)}/banco-proyectos`),
  desembolsar: (id, body) => api.post(`${coop(id)}/banco-proyectos/desembolso`, body),
  registrarRetorno: (id, proyectoId, body) => api.post(`${coop(id)}/banco-proyectos/${proyectoId}/retorno`, body),
};

export const fondosApi = {
  especiales: (id) => api.get(`${coop(id)}/fondos/especiales`),
  cierreEjercicio: (id, body) => api.post(`${coop(id)}/fondos/cierre-ejercicio`, body),
  distribuirExcedentes: (id, body) => api.post(`${coop(id)}/fondos/distribucion-excedentes`, body),
};

// ─────────────────────────────────────────────────────────────
// Afiliación: registro directo, pago de inscripción y carnet
// (ciclo: registrar → 'pendiente_pago' → pago → 'activo' + carnet).
// ─────────────────────────────────────────────────────────────
export const afiliacionApi = {
  /** dto: DatosAfiliadoDto. Devuelve el afiliado (estado 'pendiente_pago', con su valorInscripcion). */
  registrar: (cooperativaId, dto) => api.post(`${V1}/cooperativas/${cooperativaId}/afiliados`, dto),
  /** body: { monto?, metodo: 'efectivo'|'transferencia'|'consignacion'|'pasarela'|'otro', referencia?, fechaPago? }
   *  Activa al afiliado y emite su carnet → { pago, carnet, aviso? } */
  registrarPago: (afiliadoId, body) => api.post(`${V1}/afiliados/${afiliadoId}/pago-inscripcion`, body),
  pagos: (afiliadoId) => api.get(`${V1}/afiliados/${afiliadoId}/pagos`),
  /** El carnet más reciente no revocado, o null si el afiliado aún no tiene. */
  carnet: async (afiliadoId) => {
    try {
      return await api.get(`${V1}/afiliados/${afiliadoId}/carnet`);
    } catch (e) {
      if (e.status === 404) return null;
      throw e;
    }
  },
  emitirCarnet: (afiliadoId, vigenciaMeses = null) => api.post(`${V1}/afiliados/${afiliadoId}/carnet`, { vigenciaMeses }),
  cambiarEstadoCarnet: (carnetId, estado, motivo = null) => api.put(`${V1}/carnets/${carnetId}/estado`, { estado, motivo }),
  /** PÚBLICO (lo que abre el QR): no exige sesión. Un código inexistente lanza ApiError 404 con body { valido:false, ... }. */
  validarCarnet: (codigo) => apiFetch(`${V1}/carnet/validar/${encodeURIComponent(codigo)}`, { skipAuth: true }),
};

// ─────────────────────────────────────────────────────────────
// Auditoría general: quién creó, editó o eliminó cada cosa en toda la app
// (comités, cargos, nombres de afiliados/cooperativas, roles, lo que sea).
// Solo SuperAdmin y administradores con alcance sobre la entidad la ven.
// ─────────────────────────────────────────────────────────────
export const auditoriaApi = {
  /** filtro: { entidad?, accion?, usuarioId?, cooperativaId?, federacionId?, confederacionId?, desde?, hasta?, pagina, tamano } */
  listar: (filtro = {}) => {
    const qs = new URLSearchParams(Object.entries(filtro).filter(([, v]) => v !== undefined && v !== null && v !== '')).toString();
    return api.get(`${V1}/auditoria${qs ? `?${qs}` : ''}`);
  },
};

// ─────────────────────────────────────────────────────────────
// Banco de Proyectos — trámite institucional (radicar en cooperativa,
// decidir por comité, remitir a federación, ejecutar, cerrar). SEPARADO
// de proyectosApi (Financiamiento de Proyectos: desembolso contable
// directo); este es el flujo de aprobación, no mueve dinero.
// ─────────────────────────────────────────────────────────────
// Banco de Proyectos en Red de Proyectos (INTEGRACION_COOPNET_INSCRIPCION.md).
// CoopNet no guarda el proyecto: su backend verifica rol y alcance y reenvía a Red de Proyectos.
export const inscripcionProyectosApi = {
  /** → { url, expiraEn, vigenciaSegundos }: pase de un solo uso para la ventana de Red de Proyectos. */
  pedirPase: (cooperativaId) => api.post(`${V1}/banco-proyectos/inscripcion/pase`, { cooperativaId }),
  /** filtro: { cooperativaId | federacionId, estado?, pagina?, tamano? } → { total, items } */
  listar: (filtro = {}) => {
    const qs = new URLSearchParams(Object.entries(filtro).filter(([, v]) => v !== undefined && v !== null && v !== '')).toString();
    return api.get(`${V1}/banco-proyectos/inscripciones${qs ? `?${qs}` : ''}`);
  },
  detalle: (id) => api.get(`${V1}/banco-proyectos/inscripciones/${encodeURIComponent(id)}`),
};

// OBSOLETO: trámite propio de CoopNet, retirado (el Banco de Proyectos vive en Red de Proyectos).
export const bancoProyectosApi = {
  radicar: (cooperativaId, dto) => api.post(`${V1}/cooperativas/${cooperativaId}/proyectos`, dto),
  /** filtro: { cooperativaId?, federacionId?, comiteId?, tipoComite?, estado?, pagina, tamano } */
  listar: (filtro = {}) => {
    const qs = new URLSearchParams(Object.entries(filtro).filter(([, v]) => v !== undefined && v !== null && v !== '')).toString();
    return api.get(`${V1}/proyectos${qs ? `?${qs}` : ''}`);
  },
  detalle: (proyectoId) => api.get(`${V1}/proyectos/${proyectoId}`),
  decidirEnCooperativa: (proyectoId, dto) => api.post(`${V1}/proyectos/${proyectoId}/decidir-cooperativa`, dto),
  decidirEnFederacion: (proyectoId, dto) => api.post(`${V1}/proyectos/${proyectoId}/decidir-federacion`, dto),
  registrarAvance: (proyectoId, dto) => api.post(`${V1}/proyectos/${proyectoId}/avances`, dto),
  cerrar: (proyectoId) => api.post(`${V1}/proyectos/${proyectoId}/cerrar`),
};
